import type { IntervalDirection, IntervalQuestion } from '@tunasong/music-lib'
import type { NoteName } from '@tunasong/schemas'
import { type Stab, useStabScheduler } from '../player/stab-scheduler.js'

/**
 * Ear Training Interval ID audition. Three direction modes (v1.5):
 *  - Ascending / descending: two sequential single-note stabs separated by
 *    NOTE_MS + GAP_MS (~800 ms). 4n duration each.
 *  - Harmonic: one stab with both notes firing simultaneously (block dyad),
 *    half-note duration (~1200 ms) so the dyad has time to ring.
 *
 * Spec: docs/superpowers/specs/2026-05-21-ear-training-v15-design.md §5.2
 */
const NOTE_MS = 600
const GAP_MS = 200

export const buildIntervalStabs = (
  rootNote: NoteName,
  rootOctave: number,
  targetNote: NoteName,
  targetOctave: number,
  direction: IntervalDirection = 'ascending'
): Stab[] => {
  if (direction === 'harmonic') {
    return [
      {
        events: [
          { type: 'note' as const, note: `${rootNote}${rootOctave}` as NoteName, duration: '2n' },
          { type: 'note' as const, note: `${targetNote}${targetOctave}` as NoteName, duration: '2n' },
        ],
        delayMs: 0,
      },
    ]
  }
  return [
    {
      events: [{ type: 'note' as const, note: `${rootNote}${rootOctave}` as NoteName, duration: '4n' }],
      delayMs: 0,
    },
    {
      events: [{ type: 'note' as const, note: `${targetNote}${targetOctave}` as NoteName, duration: '4n' }],
      delayMs: NOTE_MS + GAP_MS,
    },
  ]
}

export interface UseIntervalPlaybackProps {
  question: IntervalQuestion | null
}

/**
 * Hook returning `{ play, stop }`. The question's `direction` field selects
 * the audition shape. A null question yields an empty stab list (no-op play),
 * so the caller can mount the hook unconditionally regardless of the active
 * sub-mode.
 */
export const useIntervalPlayback = (props: UseIntervalPlaybackProps): { play: () => void; stop: () => void } => {
  const { play, stop } = useStabScheduler({
    buildStabs: () =>
      props.question
        ? buildIntervalStabs(
            props.question.rootNote,
            props.question.rootOctave,
            props.question.targetNote,
            props.question.targetOctave,
            props.question.direction
          )
        : [],
  })
  return { play, stop }
}
